import { useState, useEffect } from "react";
import { History, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePriceHistory } from "@/hooks/usePriceHistory";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface PriceHistoryDialogProps {
  productId: string;
  productName: string;
}

export function PriceHistoryDialog({ productId, productName }: PriceHistoryDialogProps) {
  const { history, isLoading, fetchHistory } = usePriceHistory();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHistory(productId);
    }
  }, [open, productId, fetchHistory]);

  const productHistory = history.filter(h => h.product_id === productId);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getPriceChangeIcon = (oldPrice: number, newPrice: number) => {
    if (newPrice > oldPrice) {
      return <TrendingUp className="h-4 w-4 text-success" />;
    } else if (newPrice < oldPrice) {
      return <TrendingDown className="h-4 w-4 text-destructive" />;
    }
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getPriceChangePercent = (oldPrice: number, newPrice: number) => {
    if (oldPrice === 0) return 0;
    return ((newPrice - oldPrice) / oldPrice) * 100;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground">
          <History className="h-3.5 w-3.5" />
          Histórico
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de Preços - {productName}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[400px]">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Carregando...
            </div>
          ) : productHistory.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nenhuma alteração de preço registrada
            </div>
          ) : (
            <div className="space-y-3">
              {productHistory.map((entry) => {
                const changePercent = getPriceChangePercent(entry.old_price, entry.new_price);
                const isIncrease = entry.new_price > entry.old_price;
                
                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3">
                      {getPriceChangeIcon(entry.old_price, entry.new_price)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground line-through">
                            {formatCurrency(entry.old_price)}
                          </span>
                          <span className="text-sm font-medium">
                            {formatCurrency(entry.new_price)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(entry.created_at), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </div>
                    <span className={cn(
                      "text-sm font-medium",
                      isIncrease ? "text-success" : "text-destructive"
                    )}>
                      {isIncrease ? "+" : ""}{changePercent.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
